"use client";

import * as React from "react";
import { cn } from "../../lib";

export type ClickSparkEasing =
	| "linear"
	| "ease-in"
	| "ease-out"
	| "ease-in-out";

export type ClickSparkMode = "self" | "window";
export type ClickSparkTrigger = "down" | "up";

export interface ClickSparkProps {
	/**
	 * When true, choose spark color per click based on the clicked element's
	 * effective background luminance (dark bg → light sparks, light bg → dark sparks).
	 */
	autoContrast?: boolean;
	sparkColor?: string;
	sparkSize?: number;
	sparkRadius?: number;
	sparkCount?: number;
	duration?: number;
	easing?: ClickSparkEasing;
	extraScale?: number;
	/** When to spawn sparks relative to the pointer interaction (default: `"down"`). */
	trigger?: ClickSparkTrigger;
	/**
	 * How to capture clicks:
	 * - `self`: use wrapper `onClick` (wrap children)
	 * - `window`: listen to `window.pointerdown` (useful for global overlays)
	 */
	mode?: ClickSparkMode;
	/** When true, renders a fixed, full-viewport overlay (use with `mode="window"`). */
	fullscreen?: boolean;
	className?: string;
	children?: React.ReactNode;
}

interface Spark {
	x: number;
	y: number;
	angle: number;
	startTime: number;
	color: string;
}

function getEaseFunc(easing: ClickSparkEasing) {
	switch (easing) {
		case "linear":
			return (t: number) => t;
		case "ease-in":
			return (t: number) => t * t;
		case "ease-in-out":
			return (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
		default:
			return (t: number) => t * (2 - t);
	}
}

type ParsedRgba = { r: number; g: number; b: number; a: number };

const RGB_PARSE_CACHE = new Map<string, ParsedRgba | null>();
const CLICKSPARK_PRIMARY_ATTR = "data-sq-clickspark-primary";

function parseCssRgba(input: string): ParsedRgba | null {
	const cached = RGB_PARSE_CACHE.get(input);
	if (cached !== undefined) {
		return cached;
	}

	// Handles `rgb(r g b / a)` and `rgba(r, g, b, a)` and whitespace variants.
	const m =
		/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([0-9.]+))?\s*\)/i.exec(
			input,
		);
	if (!m) {
		RGB_PARSE_CACHE.set(input, null);
		return null;
	}

	const r = Number(m[1] ?? "0");
	const g = Number(m[2] ?? "0");
	const b = Number(m[3] ?? "0");
	const aRaw = m[4];
	const a = aRaw === undefined ? 1 : Number(aRaw);

	const parsed: ParsedRgba = {
		r: Number.isFinite(r) ? r : 0,
		g: Number.isFinite(g) ? g : 0,
		b: Number.isFinite(b) ? b : 0,
		a: Number.isFinite(a) ? a : 1,
	};

	RGB_PARSE_CACHE.set(input, parsed);
	return parsed;
}

function parseCssHex(input: string): ParsedRgba | null {
	const hex = input.trim();
	const m = /^#([0-9a-f]{6})$/i.exec(hex);
	if (!m) {
		return null;
	}
	const n = m[1] ?? "";
	const r = Number.parseInt(n.slice(0, 2), 16);
	const g = Number.parseInt(n.slice(2, 4), 16);
	const b = Number.parseInt(n.slice(4, 6), 16);
	return { r, g, b, a: 1 };
}

function parseCssColor(input: string): ParsedRgba | null {
	return parseCssHex(input) ?? parseCssRgba(input);
}

function srgbToLinear(c: number): number {
	// c in [0..1]
	return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
	const r = srgbToLinear(Math.min(1, Math.max(0, rgb.r / 255)));
	const g = srgbToLinear(Math.min(1, Math.max(0, rgb.g / 255)));
	const b = srgbToLinear(Math.min(1, Math.max(0, rgb.b / 255)));
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function findClickSparkPrimaryOverride(el: Element | null): ParsedRgba | null {
	let node: Element | null = el;
	for (let i = 0; node && i < 12; i += 1) {
		const raw = node.getAttribute(CLICKSPARK_PRIMARY_ATTR);
		if (typeof raw === "string" && raw.trim().length > 0) {
			const parsed = parseCssColor(raw.trim());
			if (parsed) {
				return parsed;
			}
			return null;
		}
		node = node.parentElement;
	}
	return null;
}

function findEffectiveBackgroundColor(el: Element | null): ParsedRgba | null {
	let node: Element | null = el;
	// Keep bounded: avoids pathological DOM walks on click.
	for (let i = 0; node && i < 12; i += 1) {
		const bg = window.getComputedStyle(node).backgroundColor;
		const parsed = parseCssRgba(bg);
		if (parsed && parsed.a > 0.05) {
			return parsed;
		}
		node = node.parentElement;
	}
	return null;
}

function resolveAutoContrastColor(clientX: number, clientY: number): string {
	// Use `elementsFromPoint` so we can detect override attributes on visual layers that
	// don't participate in hit-testing (e.g. `pointer-events:none` backgrounds like Grainient).
	const els = document.elementsFromPoint(clientX, clientY);
	let override: ParsedRgba | null = null;
	for (const el of els) {
		override = findClickSparkPrimaryOverride(el);
		if (override) {
			break;
		}
	}
	if (override) {
		const lum = relativeLuminance(override);
		return lum < 0.5 ? "#fff" : "#111";
	}
	const primaryEl = els[0] ?? null;
	const bg = findEffectiveBackgroundColor(primaryEl);
	if (!bg) {
		return "#111";
	}
	const lum = relativeLuminance(bg);
	return lum < 0.5 ? "#fff" : "#111";
}

export function ClickSpark({
	autoContrast = false,
	sparkColor = "#fff",
	sparkSize = 10,
	sparkRadius = 15,
	sparkCount = 8,
	duration = 400,
	easing = "ease-out",
	extraScale = 1,
	trigger = "down",
	mode = "self",
	fullscreen = false,
	className,
	children,
}: ClickSparkProps) {
	const wrapperRef = React.useRef<HTMLDivElement | null>(null);
	const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
	const sparksRef = React.useRef<Spark[]>([]);

	const easeFunc = React.useMemo(() => getEaseFunc(easing), [easing]);

	React.useEffect(() => {
		const canvas = canvasRef.current;
		const wrapper = wrapperRef.current;
		if (!canvas || !wrapper) {
			return;
		}

		let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
		const resizeCanvas = () => {
			const rect = wrapper.getBoundingClientRect();
			const width = Math.max(1, Math.floor(rect.width));
			const height = Math.max(1, Math.floor(rect.height));
			if (canvas.width !== width || canvas.height !== height) {
				canvas.width = width;
				canvas.height = height;
			}
		};

		const handleResize = () => {
			if (resizeTimeout) {
				clearTimeout(resizeTimeout);
			}
			resizeTimeout = setTimeout(resizeCanvas, 100);
		};

		const ro = new ResizeObserver(handleResize);
		ro.observe(wrapper);
		resizeCanvas();

		return () => {
			ro.disconnect();
			if (resizeTimeout) {
				clearTimeout(resizeTimeout);
			}
		};
	}, []);

	React.useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}
		const ctx = canvas.getContext("2d");
		if (!ctx) {
			return;
		}

		let animationId = 0;
		const draw = (timestamp: number) => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			sparksRef.current = sparksRef.current.filter((spark) => {
				const elapsed = timestamp - spark.startTime;
				if (elapsed >= duration) {
					return false;
				}

				const progress = elapsed / duration;
				const eased = easeFunc(progress);

				const distance = eased * sparkRadius * extraScale;
				const lineLength = sparkSize * (1 - eased);

				const x1 = spark.x + distance * Math.cos(spark.angle);
				const y1 = spark.y + distance * Math.sin(spark.angle);
				const x2 =
					spark.x + (distance + lineLength) * Math.cos(spark.angle);
				const y2 =
					spark.y + (distance + lineLength) * Math.sin(spark.angle);

				ctx.strokeStyle = spark.color;
				ctx.lineWidth = 2;
				ctx.beginPath();
				ctx.moveTo(x1, y1);
				ctx.lineTo(x2, y2);
				ctx.stroke();

				return true;
			});

			animationId = window.requestAnimationFrame(draw);
		};

		animationId = window.requestAnimationFrame(draw);
		return () => {
			window.cancelAnimationFrame(animationId);
		};
	}, [sparkSize, sparkRadius, sparkCount, duration, easeFunc, extraScale]);

	const pushSparksAtClientPoint = React.useCallback(
		(clientX: number, clientY: number) => {
			const canvas = canvasRef.current;
			if (!canvas) {
				return;
			}
			const rect = canvas.getBoundingClientRect();
			const x = clientX - rect.left;
			const y = clientY - rect.top;

			const resolvedColor = autoContrast
				? resolveAutoContrastColor(clientX, clientY)
				: sparkColor;

			const now = performance.now();
			const newSparks: Spark[] = Array.from(
				{ length: sparkCount },
				(_, i) => ({
					x,
					y,
					angle: (2 * Math.PI * i) / sparkCount,
					startTime: now,
					color: resolvedColor,
				}),
			);

			sparksRef.current.push(...newSparks);
		},
		[autoContrast, sparkColor, sparkCount],
	);

	const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
		pushSparksAtClientPoint(e.clientX, e.clientY);
	};

	const handlePointerEvent = (
		e: React.PointerEvent<HTMLDivElement>,
	): void => {
		pushSparksAtClientPoint(e.clientX, e.clientY);
	};

	React.useEffect(() => {
		if (mode !== "window") {
			return;
		}

		const onPointer = (e: PointerEvent) => {
			// Only left click / primary touch.
			if (typeof e.button === "number" && e.button !== 0) {
				return;
			}
			pushSparksAtClientPoint(e.clientX, e.clientY);
		};

		const eventName = trigger === "up" ? "pointerup" : "pointerdown";
		window.addEventListener(eventName, onPointer, { passive: true });
		return () => {
			window.removeEventListener(eventName, onPointer);
		};
	}, [mode, pushSparksAtClientPoint, trigger]);

	return (
		<div
			ref={wrapperRef}
			className={cn(
				"relative",
				fullscreen
					? "fixed inset-0 z-[999] pointer-events-none"
					: "h-full w-full",
				className,
			)}
			onClick={
				mode === "self" && trigger === "up" ? handleClick : undefined
			}
			onPointerDown={
				mode === "self" && trigger === "down"
					? handlePointerEvent
					: undefined
			}
			onPointerUp={
				mode === "self" && trigger === "up"
					? handlePointerEvent
					: undefined
			}
		>
			<canvas
				ref={canvasRef}
				className="absolute inset-0 pointer-events-none"
			/>
			{children}
		</div>
	);
}

export default ClickSpark;
